import { Suspense } from "react";
import { trpc } from "@/utils/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import "./index.css";
import "@tabler/icons-webfont/dist/tabler-icons.css";

// Initialize i18n
import "./lib/i18n";

console.log("[INIT] App initialization starting");

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;
  if (!isUnauthorized) return;

  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

// === CRITICAL CHANGE START ===
// In production with same-domain setup, use relative URLs
// In development, use localhost:3000
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
  || (import.meta.env.DEV ? 'http://localhost:3000' : '');
// === CRITICAL CHANGE END ===

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      // Updated URL
      url: `${API_BASE_URL}/api/trpc`,
      transformer: superjson,
      headers() {
        const sessionToken = localStorage.getItem('app_session_token');
        if (sessionToken) {
          return {
            Authorization: `Bearer ${sessionToken}`,
          };
        }
        return {};
      },
      fetch(input, init) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        return globalThis
          .fetch(input, {
            ...(init ?? {}),
            credentials: "include",
            signal: controller.signal,
          })
          .finally(() => clearTimeout(timeoutId));
      },
    }),
  ],
});

const LoadingFallback = () => (
  <div className="flex h-screen w-screen items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <span className="text-[#6B6B6B]">Loading...</span>
    </div>
  </div>
);

createRoot(document.getElementById("root")!).render(
  <Suspense fallback={<LoadingFallback />}>
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </trpc.Provider>
  </Suspense>
);

// Load analytics if endpoint is configured (avoiding URL substitution errors)
(() => {
  const endpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
  const websiteId = import.meta.env.VITE_ANALYTICS_WEBSITE_ID;

  if (endpoint && websiteId && endpoint !== 'http://localhost:3000') {
    const script = document.createElement('script');
    script.defer = true;
    script.src = `${endpoint}/umami`;
    script.setAttribute('data-website-id', websiteId);
    script.onerror = () => console.warn('[Analytics] Failed to load from', endpoint);
    document.head.appendChild(script);
  }
})();
